import React from 'react';
import { styled } from '@linaria/react';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useSpring, animated } from 'react-spring';
import { motion } from 'framer-motion';

import { roundTo } from '@/utils';

interface ArrowProps extends React.ComponentProps<typeof motion.div> {
  distance: number;
  animateTransitions?: boolean | 'label-only';
  // Typically, text sits underneath the horizontal arrow, but with this prop, I can flip it to the top.
  flipTextSide?: boolean;
  // Sometimes I want to multiply the displayed value. For example, in EmailLayoutDemo, I'm showing a zoomed-out desktop UI, so the displayed distance should be much larger than their real values.
  multiple?: number;
  unit?: 'px' | 'rem';
}

interface Props extends ArrowProps {
  axis: 'horizontal' | 'vertical';
}

function ArrowShowingDistance({
  distance,
  animateTransitions,
  flipTextSide,
  multiple = 1,
  axis,
  unit = 'px',
  ...delegated
}: Props) {
  const Component = axis === 'horizontal' ? LeftArrow : TopArrow;

  return (
    <Component
      distance={distance}
      animateTransitions={animateTransitions}
      flipTextSide={flipTextSide}
      multiple={multiple}
      unit={unit}
      {...delegated}
    />
  );
}

function LeftArrow({
  distance,
  animateTransitions,
  flipTextSide,
  multiple = 1,
  unit = 'px',
  ...delegated
}: ArrowProps) {
  const spring = useSpring({
    from: { number: animateTransitions === true ? 0 : distance },
    number: distance,
    immediate: !animateTransitions,
  });

  return (
    <Wrapper
      // More issues with TS not recognizing `as`.
      // @ts-ignore
      as={animateTransitions === true ? motion.div : 'div'}
      style={{
        width: animateTransitions === true ? undefined : distance,
        height: 20,
      }}
      animate={
        animateTransitions === true
          ? {
              width: distance,
            }
          : undefined
      }
      {...delegated}
    >
      <ArrowWrapper>
        <HorizontalArrowElem>
          <Left size={20} />
          <Right size={20} />
          <HorizontalShaft />
        </HorizontalArrowElem>
      </ArrowWrapper>
      <HorizontalLabel style={{ top: flipTextSide ? -16 : 16 }}>
        {spring.number.to((n: number) => {
          const rounded =
            unit === 'px'
              ? Math.round(n * multiple)
              : roundTo((n / 16) * multiple, 1).toFixed(1);

          const hideUnit = n < 40;

          // For horizontal arrows, we don't have enough space to show '20px'. Just show the number instead.
          if (hideUnit) {
            return rounded;
          }

          return rounded + unit;
        })}
      </HorizontalLabel>
    </Wrapper>
  );
}

function TopArrow({
  distance,
  animateTransitions,
  multiple = 1,
  flipTextSide,
  ...delegated
}: ArrowProps) {
  const spring = useSpring({
    from: { number: 0 },
    number: distance,
    immediate: !animateTransitions,
  });

  return (
    <Wrapper
      // More issues with TS not recognizing `as`.
      // @ts-ignore
      as={animateTransitions ? motion.div : 'div'}
      style={{
        width: 20,
        height: animateTransitions ? undefined : distance,
      }}
      animate={{
        height: animateTransitions ? distance : undefined,
      }}
      {...delegated}
    >
      <ArrowWrapper style={{ opacity: distance < 16 ? 0 : 1 }}>
        <VerticalArrowElem>
          <Up size={20} />
          <Down size={20} />
          <VerticalShaft />
        </VerticalArrowElem>
      </ArrowWrapper>
      <VerticalLabel>
        {spring.number.to(
          (n: number) => Math.round(n * multiple) + 'px'
        )}
      </VerticalLabel>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  position: relative;
`;

const ArrowWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  transition: opacity 200ms;
`;

const ArrowElem = styled.div`
  width: 100%;
  height: 100%;
  color: var(--color-gray-700);
  transform-origin: bottom center;
  backface-visibility: hidden;
  will-change: transform;
`;

const VerticalArrowElem = styled(ArrowElem)`
  animation: verticalEnter 250ms;

  @keyframes verticalEnter {
    from {
      transform: translateY(25%) scaleY(0.5);
      opacity: 0;
    }
    to {
      transform: translateY(0px) scaleY(1);
      opacity: 1;
    }
  }
`;
const HorizontalArrowElem = styled(ArrowElem)`
  animation: horizontalEnter 250ms;

  @keyframes horizontalEnter {
    from {
      transform: translateX(25%) scaleX(0.5);
      opacity: 0;
    }
    to {
      transform: translateX(0px) scaleX(1);
      opacity: 1;
    }
  }
`;

const Up = styled(ChevronUp)`
  position: absolute;
  top: -6px;
`;
const Down = styled(ChevronDown)`
  position: absolute;
  bottom: -6px;
`;
const Left = styled(ChevronLeft)`
  position: absolute;
  left: -6px;
`;
const Right = styled(ChevronRight)`
  position: absolute;
  right: -6px;
`;

const Shaft = styled.div`
  position: absolute;
  margin: auto;
  background: var(--color-gray-700);
  border-radius: 10px;
`;

const VerticalShaft = styled(Shaft)`
  top: 2px;
  bottom: 2px;
  left: 0;
  right: 0;
  width: 2px;
`;
const HorizontalShaft = styled(Shaft)`
  top: 0;
  bottom: 0;
  left: 2px;
  right: 2px;
  height: 2px;
`;

const Label = styled(animated.span)`
  position: absolute;
  height: 20px;
  line-height: 20px;
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  margin: auto;
`;

const VerticalLabel = styled(Label)`
  top: 0;
  bottom: 0;
  left: 28px;
`;
const HorizontalLabel = styled(Label)`
  left: 0;
  right: 0;
  text-align: center;
`;

export default ArrowShowingDistance;
